if (!customElements.get('admin-barter-proposals')) {
  class AdminBarterProposals extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.endpoint = (this.dataset.endpoint || '').trim();
      this.submissionsEndpoint = (this.dataset.submissionsEndpoint || '').trim();
      this.currency = this.dataset.currency || 'USD';
      this.proposals = [];
      this.submissions = [];
      this.hasProposalList = Boolean(this.select('[data-role="proposal-rows"]'));
      this.hasSubmissionLists = Boolean(
        this.select('[data-role="tax-submission-rows"]') || this.select('[data-role="contact-submission-rows"]')
      );
      this.addEventListener('click', this.handleClick.bind(this));
      if (this.hasProposalList) this.loadProposals();
      if (this.hasSubmissionLists) this.loadSubmissions();
    }

    select(selector) {
      return this.querySelector(selector);
    }

    handleClick(event) {
      const refreshButton = event.target.closest('[data-role="refresh-proposals"]');
      if (refreshButton) {
        event.preventDefault();
        this.loadProposals();
        return;
      }

      const refreshSubmissionsButton = event.target.closest('[data-role="refresh-submissions"]');
      if (refreshSubmissionsButton) {
        event.preventDefault();
        this.loadSubmissions();
        return;
      }

      const decisionButton = event.target.closest('[data-role="decision-button"]');
      if (decisionButton && !decisionButton.disabled) {
        event.preventDefault();
        this.saveProposal(decisionButton, decisionButton.dataset.status || 'reviewing');
      }
    }

    async loadSubmissions() {
      if (!this.submissionsEndpoint) {
        this.showSubmissionNotice('tax_exemption', 'error', 'Submission endpoint is not configured.');
        this.showSubmissionNotice('contact', 'error', 'Submission endpoint is not configured.');
        return;
      }

      this.showSubmissionNotice('tax_exemption', 'info', 'Loading tax exemption requests...');
      this.showSubmissionNotice('contact', 'info', 'Loading contact messages...');

      try {
        const response = await fetch(this.submissionsEndpoint, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        const result = await response.json().catch(() => ({}));

        if (!response.ok || !result.ok) {
          throw new Error(result.error || `Backend returned ${response.status}`);
        }

        this.submissions = Array.isArray(result.submissions) ? result.submissions : [];
        this.renderSubmissionRows('tax_exemption');
        this.renderSubmissionRows('contact');
        this.showSubmissionNotice('tax_exemption', 'success', `Loaded ${this.getSubmissionsByType('tax_exemption').length} tax exemption request${this.getSubmissionsByType('tax_exemption').length === 1 ? '' : 's'}.`);
        this.showSubmissionNotice('contact', 'success', `Loaded ${this.getSubmissionsByType('contact').length} contact/custom pricing message${this.getSubmissionsByType('contact').length === 1 ? '' : 's'}.`);
      } catch (error) {
        console.error(error);
        this.renderSubmissionRows('tax_exemption', []);
        this.renderSubmissionRows('contact', []);
        this.showSubmissionNotice('tax_exemption', 'error', error.message || 'Could not load tax exemption requests.');
        this.showSubmissionNotice('contact', 'error', error.message || 'Could not load contact messages.');
      }
    }

    async loadProposals() {
      if (!this.endpoint) {
        this.showNotice('error', 'Admin proposal endpoint is not configured.');
        return;
      }

      this.showNotice('info', 'Loading barter proposals...');

      try {
        const response = await fetch(this.endpoint, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        const result = await response.json().catch(() => ({}));

        if (!response.ok || !result.ok) {
          throw new Error(result.error || `Backend returned ${response.status}`);
        }

        this.proposals = Array.isArray(result.proposals) ? result.proposals : [];
        this.renderMetrics(result.summary || this.calculateSummary(this.proposals));
        this.renderRows();
        this.showNotice('success', `Loaded ${this.proposals.length} proposal${this.proposals.length === 1 ? '' : 's'}.`);
      } catch (error) {
        console.error(error);
        this.renderMetrics({ new: 0, reviewing: 0, accepted: 0, declined: 0 });
        this.renderRows([]);
        this.showNotice('error', error.message || 'Could not load barter proposals.');
      }
    }

    async saveProposal(button, status) {
      const card = button.closest('[data-proposal-id]');
      const proposalId = card?.dataset.proposalId || '';
      if (!proposalId || !this.endpoint) return;

      const payload = this.getUpdatePayload(card, status);
      if (!payload) return;

      const previousLabel = button.textContent;
      this.setCardBusy(card, true);
      button.textContent = 'Saving...';
      this.showNotice('info', 'Saving proposal decision...');

      try {
        const response = await fetch(`${this.endpoint}/${encodeURIComponent(proposalId)}`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'content-type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });
        const result = await response.json().catch(() => ({}));

        if (!response.ok || !result.ok) {
          throw new Error(result.error || `Backend returned ${response.status}`);
        }

        this.proposals = this.proposals.map((proposal) =>
          proposal.id === proposalId ? result.proposal : proposal
        );
        this.renderMetrics(this.calculateSummary(this.proposals));
        this.renderRows();
        const discountSuffix =
          result.proposal.discount_code && result.proposal.status === 'accepted'
            ? ` Discount code ${result.proposal.discount_code} is ready.`
            : '';
        this.showNotice(
          'success',
          `${this.getStatusLabel(result.proposal.status)} decision saved. The customer page will now show this status.${discountSuffix}`
        );
      } catch (error) {
        console.error(error);
        this.setCardBusy(card, false);
        button.textContent = previousLabel;
        this.showNotice('error', error.message || 'Could not save proposal decision.');
      }
    }

    getUpdatePayload(card, status) {
      const counterInput = card.querySelector('[data-role="counter-amount"]');
      const discountInput = card.querySelector('[data-role="discount-code"]');
      const noteInput = card.querySelector('[data-role="admin-note"]');
      const counterValue = (counterInput?.value || '').replace(/[^0-9.]/g, '');
      const counterAmountCents = counterValue ? Math.round(parseFloat(counterValue) * 100) : null;

      if (status === 'counter' && (!counterAmountCents || counterAmountCents < 1)) {
        this.showNotice('error', 'Enter a counter amount before sending a counter offer.');
        if (counterInput) counterInput.focus();
        return null;
      }

      return {
        status,
        admin_note: (noteInput?.value || this.getDefaultMessage(status)).trim(),
        discount_code: (discountInput?.value || '').trim(),
        counter_amount_cents: counterAmountCents,
      };
    }

    getDefaultMessage(status) {
      if (status === 'accepted') return 'Your barter proposal has been accepted.';
      if (status === 'declined') return 'We were not able to accept this proposal at the requested price.';
      if (status === 'counter') return 'We reviewed your request and sent a counter offer.';
      return '';
    }

    setCardBusy(card, isBusy) {
      card.querySelectorAll('button, input, textarea').forEach((control) => {
        control.disabled = isBusy;
      });
      card.toggleAttribute('aria-busy', isBusy);
    }

    renderMetrics(summary) {
      this.setMetric('new', summary.new || 0);
      this.setMetric('reviewing', summary.reviewing || 0);
      this.setMetric('accepted', summary.accepted || 0);
      this.setMetric('declined', summary.declined || 0);
    }

    setMetric(name, value) {
      const node = this.select(`[data-role="metric-${name}"]`);
      if (node) node.textContent = String(value);
    }

    calculateSummary(proposals) {
      return proposals.reduce(
        (summary, proposal) => {
          if (proposal.status === 'accepted' || proposal.status === 'customer_accepted') summary.accepted += 1;
          else if (proposal.status === 'declined' || proposal.status === 'customer_declined') summary.declined += 1;
          else if (['reviewing', 'counter', 'customer_countered'].includes(proposal.status)) summary.reviewing += 1;
          else summary.new += 1;
          return summary;
        },
        { new: 0, reviewing: 0, accepted: 0, declined: 0 }
      );
    }

    renderRows(proposals = this.proposals) {
      const container = this.select('[data-role="proposal-rows"]');
      const empty = this.select('[data-role="proposal-empty"]');
      if (!container) return;

      if (!proposals.length) {
        container.innerHTML = '';
        if (empty) empty.hidden = false;
        return;
      }

      if (empty) empty.hidden = true;
      container.innerHTML = proposals.map((proposal) => this.renderProposalCard(proposal)).join('');
    }

    getSubmissionsByType(type) {
      if (type === 'contact') {
        return this.submissions.filter((submission) =>
          ['contact', 'custom_pricing'].includes(submission.submission_type)
        );
      }

      return this.submissions.filter((submission) => submission.submission_type === type);
    }

    renderSubmissionRows(type, submissions = this.getSubmissionsByType(type)) {
      const container = this.select(`[data-role="${type === 'tax_exemption' ? 'tax-submission-rows' : 'contact-submission-rows'}"]`);
      const empty = this.select(`[data-role="${type === 'tax_exemption' ? 'tax-submission-empty' : 'contact-submission-empty'}"]`);
      if (!container) return;

      if (!submissions.length) {
        container.innerHTML = '';
        if (empty) empty.hidden = false;
        return;
      }

      if (empty) empty.hidden = true;
      container.innerHTML = submissions.map((submission) => this.renderSubmissionCard(submission)).join('');
    }

    renderSubmissionCard(submission) {
      const payload = submission.payload || {};
      const files = Array.isArray(payload.files) ? payload.files : [];
      const fields = payload.fields || {};
      const typeLabel =
        submission.submission_type === 'tax_exemption'
          ? 'Tax exemption'
          : submission.submission_type === 'custom_pricing'
            ? 'Custom pricing fallback'
            : 'Contact';
      const company = payload.company || fields['Company/Organization Name'] || '';
      const state = payload.state || fields.State || '';

      return `
        <article class="wl-admin-proposal wl-admin-proposal--reviewing" data-submission-id="${this.escapeAttribute(submission.id)}">
          <header class="wl-admin-proposal__header">
            <div>
              <div class="wl-admin-proposal__kicker">
                <span class="wl-admin__status wl-admin__status--reviewing">${this.escapeHtml(typeLabel)}</span>
                <span>${this.escapeHtml(this.formatDate(submission.created_at))}</span>
                <span>${this.escapeHtml(submission.request_id || '')}</span>
              </div>
              <h3 class="wl-admin-proposal__title">${this.escapeHtml(submission.subject || typeLabel)}</h3>
              <p class="wl-admin-proposal__subtitle">${this.escapeHtml(submission.customer_name || 'Customer')} &middot; ${this.escapeHtml(submission.customer_email || '')}</p>
            </div>
          </header>

          <div class="wl-admin-proposal__body wl-admin-proposal__body--simple">
            <section class="wl-admin-proposal__section" aria-label="${this.escapeAttribute(typeLabel)} details">
              <div class="wl-admin-proposal__section-head">
                <h4>Submission details</h4>
                <span>${this.escapeHtml(this.formatDateTime(submission.created_at))}</span>
              </div>
              <dl class="wl-admin-proposal__facts">
                <div><dt>Email</dt><dd>${this.escapeHtml(submission.customer_email || 'Not provided')}</dd></div>
                <div><dt>Phone</dt><dd>${this.escapeHtml(submission.customer_phone || 'Not provided')}</dd></div>
                ${company ? `<div><dt>Company</dt><dd>${this.escapeHtml(company)}</dd></div>` : ''}
                ${state ? `<div><dt>State</dt><dd>${this.escapeHtml(state)}</dd></div>` : ''}
                ${payload.source_url ? `<div><dt>Source page</dt><dd><a href="${this.escapeAttribute(payload.source_url)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(payload.source_url)}</a></dd></div>` : ''}
              </dl>
              ${
                submission.message
                  ? `<div class="wl-admin-proposal__message">
                      <span>Message</span>
                      <p>${this.escapeHtml(submission.message)}</p>
                    </div>`
                  : ''
              }
              ${this.renderFileList(files)}
            </section>
          </div>
        </article>
      `;
    }

    renderProposalCard(proposal) {
      const payload = proposal.payload || {};
      const preview = proposal.preview || {};
      const items = Array.isArray(payload.items) ? payload.items : [];
      const files = Array.isArray(payload.project?.files) ? payload.project.files : [];
      const projectMessage = proposal.project_message || payload.project?.message || '';
      const customerResponse = payload.customer_response || null;
      const discountError = payload.discount_error || null;
      const discountApplyUrl = proposal.discount_apply_url || payload.discount?.apply_url || '';
      const counterAmount = proposal.counter_amount_cents || 0;
      const statusClass = this.getStatusClass(proposal.status);

      return `
        <article class="wl-admin-proposal wl-admin-proposal--${this.escapeAttribute(statusClass)}" data-proposal-id="${this.escapeAttribute(proposal.id)}">
          <header class="wl-admin-proposal__header">
            <div>
              <div class="wl-admin-proposal__kicker">
                <span class="wl-admin__status wl-admin__status--${this.escapeAttribute(statusClass)}">${this.escapeHtml(this.getStatusLabel(proposal.status))}</span>
                <span>${this.escapeHtml(this.formatDate(proposal.created_at))}</span>
                <span>${this.escapeHtml(proposal.request_id || '')}</span>
              </div>
              <h3 class="wl-admin-proposal__title">${this.escapeHtml(preview.title || 'Barter request')}</h3>
              <p class="wl-admin-proposal__subtitle">${this.escapeHtml(proposal.customer_name || 'Customer')} &middot; ${this.escapeHtml(proposal.customer_email || '')}</p>
            </div>
            <div class="wl-admin-proposal__money">
              <span class="wl-admin-proposal__money-label">Customer offer</span>
              <strong>${this.formatMoney(proposal.customer_offer_total_cents, proposal.currency)}</strong>
              <span>List ${this.formatMoney(proposal.original_total_cents, proposal.currency)}</span>
            </div>
          </header>

          <div class="wl-admin-proposal__body">
            <section class="wl-admin-proposal__section wl-admin-proposal__section--items" aria-label="Requested items">
              <div class="wl-admin-proposal__section-head">
                <h4>Items requested</h4>
                <span>${this.escapeHtml(preview.item_summary || `${proposal.item_count || 0} items`)}</span>
              </div>
              ${this.renderItemList(items, proposal.currency)}
            </section>

            <section class="wl-admin-proposal__section" aria-label="Customer and project details">
              <div class="wl-admin-proposal__section-head">
                <h4>Customer details</h4>
              </div>
              <dl class="wl-admin-proposal__facts">
                <div><dt>Email</dt><dd>${this.escapeHtml(proposal.customer_email || 'Not provided')}</dd></div>
                <div><dt>Phone</dt><dd>${this.escapeHtml(proposal.customer_phone || 'Not provided')}</dd></div>
                <div><dt>Customer ID</dt><dd>${this.escapeHtml(proposal.customer_id || 'Not signed in')}</dd></div>
                <div><dt>Submitted</dt><dd>${this.escapeHtml(this.formatDateTime(proposal.created_at))}</dd></div>
              </dl>
              ${this.renderCustomerResponse(customerResponse, proposal.currency)}
              ${
                projectMessage
                  ? `<div class="wl-admin-proposal__message">
                      <span>Project message</span>
                      <p>${this.escapeHtml(projectMessage)}</p>
                    </div>`
                  : ''
              }
              ${this.renderFileList(files)}
            </section>

            <section class="wl-admin-proposal__section wl-admin-proposal__section--decision" aria-label="Admin decision">
              <div class="wl-admin-proposal__section-head">
                <h4>Decision</h4>
                <span>Updates customer page</span>
              </div>
              <div class="wl-admin-proposal__totals">
                <div>
                  <span>Requested discount</span>
                  <strong>${this.escapeHtml(String(proposal.discount_requested_pct || 0))}%</strong>
                  <small>${this.formatMoney(proposal.discount_requested_cents, proposal.currency)}</small>
                </div>
                <div>
                  <span>Counter</span>
                  <strong>${counterAmount ? this.formatMoney(counterAmount, proposal.currency) : '--'}</strong>
                  <small>${counterAmount ? 'Visible to customer' : 'Not set'}</small>
                </div>
                <div>
                  <span>Discount</span>
                  <strong>${proposal.discount_code ? this.escapeHtml(proposal.discount_code) : '--'}</strong>
                  <small>${discountApplyUrl ? 'Apply link ready' : proposal.discount_code ? 'Code saved' : 'Not created yet'}</small>
                </div>
              </div>
              ${this.renderDiscountError(discountError)}
              <div class="wl-admin-proposal__fields">
                <label class="wl-admin__field-label">
                  <span>Counter amount</span>
                  <input class="wl-admin__field" type="text" inputmode="decimal" data-role="counter-amount" value="${this.escapeAttribute(this.centsToInputValue(counterAmount))}" placeholder="0.00">
                </label>
                <label class="wl-admin__field-label">
                  <span>Discount code</span>
                  <input class="wl-admin__field" type="text" data-role="discount-code" value="${this.escapeAttribute(proposal.discount_code || '')}" placeholder="Auto-created on accept">
                  <small class="wl-admin__field-help">Leave blank to generate a one-use Shopify code.</small>
                </label>
                <label class="wl-admin__field-label">
                  <span>Customer-facing message</span>
                  <textarea class="wl-admin__field wl-admin__field--textarea" data-role="admin-note" rows="4" placeholder="Short note the customer should see on the barter page">${this.escapeHtml(proposal.admin_note || '')}</textarea>
                </label>
              </div>
              <div class="wl-admin-proposal__actions">
                <button type="button" class="wl-admin__button wl-admin__button--accept" data-role="decision-button" data-status="accepted">Accept offer</button>
                <button type="button" class="wl-admin__button wl-admin__button--counter" data-role="decision-button" data-status="counter">Send counter</button>
                <button type="button" class="wl-admin__button wl-admin__button--reject" data-role="decision-button" data-status="declined">Reject</button>
                <button type="button" class="wl-admin__button wl-admin__button--secondary" data-role="decision-button" data-status="reviewing">Save note</button>
              </div>
            </section>
          </div>
        </article>
      `;
    }

    renderDiscountError(discountError) {
      if (!discountError || typeof discountError !== 'object') return '';

      return `
        <div class="wl-admin-proposal__message wl-admin-proposal__message--warning">
          <span>Discount creation needs attention</span>
          <p>${this.escapeHtml(discountError.message || 'Shopify discount code was not created. Save the accepted decision again after checking discount API credentials and scopes.')}</p>
        </div>
      `;
    }

    renderItemList(items, currency) {
      if (!items.length) {
        return '<p class="wl-admin-proposal__muted">No item details were saved with this proposal.</p>';
      }

      return `
        <ul class="wl-admin-proposal__items" role="list">
          ${items
            .map(
              (item) => `
                <li>
                  <div>
                    <strong>${this.escapeHtml(item.product_title || 'Product')}</strong>
                    ${item.variant_title ? `<span>${this.escapeHtml(item.variant_title)}</span>` : ''}
                    ${item.sku ? `<span>SKU ${this.escapeHtml(item.sku)}</span>` : ''}
                  </div>
                  <div>
                    <strong>${this.escapeHtml(String(item.quantity || 1))} x ${this.formatMoney(item.original_unit_price_cents, currency)}</strong>
                    <span>${this.formatMoney(item.original_line_price_cents, currency)}</span>
                  </div>
                </li>
              `
            )
            .join('')}
        </ul>
      `;
    }

    renderCustomerResponse(customerResponse, currency) {
      if (!customerResponse || typeof customerResponse !== 'object') return '';

      const responseLabel =
        {
          accept: 'Customer accepted counter',
          reject: 'Customer rejected counter',
          counter: 'Customer countered',
        }[customerResponse.response] || 'Customer response';
      const counterAmount = customerResponse.counter_amount_cents
        ? this.formatMoney(customerResponse.counter_amount_cents, currency)
        : '';
      const message = (customerResponse.message || '').trim();
      const respondedAt = this.formatDateTime(customerResponse.responded_at);

      return `
        <div class="wl-admin-proposal__customer-response">
          <span>${this.escapeHtml(responseLabel)}</span>
          <dl>
            ${
              counterAmount
                ? `<div>
                    <dt>Counter amount</dt>
                    <dd>${this.escapeHtml(counterAmount)}</dd>
                  </div>`
                : ''
            }
            <div>
              <dt>Responded</dt>
              <dd>${this.escapeHtml(respondedAt)}</dd>
            </div>
            ${
              message
                ? `<div>
                    <dt>Message</dt>
                    <dd>${this.escapeHtml(message)}</dd>
                  </div>`
                : ''
            }
          </dl>
        </div>
      `;
    }

    renderFileList(files) {
      if (!files.length) return '';

      return `
        <div class="wl-admin-proposal__message">
          <span>Project files</span>
          <ul class="wl-admin-proposal__files" role="list">
            ${files
              .map((file) => {
                const dropboxUrl = this.getSafeDropboxUrl(file);
                const fileName = this.escapeHtml(file.name || 'File');
                const fileMeta = this.escapeHtml([file.size || '', file.type || ''].filter(Boolean).join(' | '));
                const label = dropboxUrl
                  ? `<a href="${this.escapeAttribute(dropboxUrl)}" target="_blank" rel="noopener noreferrer">${fileName}</a>`
                  : fileName;
                return `<li>${label} <span>${fileMeta}</span></li>`;
              })
              .join('')}
          </ul>
        </div>
      `;
    }

    getStatusClass(status) {
      if (status === 'accepted' || status === 'customer_accepted') return 'accepted';
      if (status === 'declined' || status === 'customer_declined') return 'declined';
      if (status === 'reviewing') return 'reviewing';
      if (status === 'counter' || status === 'customer_countered') return 'counter';
      return 'new';
    }

    getStatusLabel(status) {
      if (status === 'accepted') return 'Accepted';
      if (status === 'declined') return 'Rejected';
      if (status === 'reviewing') return 'Reviewing';
      if (status === 'counter') return 'Counter sent';
      if (status === 'customer_accepted') return 'Customer accepted';
      if (status === 'customer_declined') return 'Customer rejected';
      if (status === 'customer_countered') return 'Customer countered';
      return 'New';
    }

    showNotice(type, message) {
      const notice = this.select('[data-role="admin-proposals-status"]');
      if (!notice) return;
      notice.hidden = false;
      notice.className = `wl-admin__notice wl-admin__notice--${type}`;
      notice.textContent = message;
    }

    showSubmissionNotice(submissionType, type, message) {
      const notice = this.select(
        `[data-role="${submissionType === 'tax_exemption' ? 'tax-submissions-status' : 'contact-submissions-status'}"]`
      );
      if (!notice) return;
      notice.hidden = false;
      notice.className = `wl-admin__notice wl-admin__notice--${type}`;
      notice.textContent = message;
    }

    centsToInputValue(cents) {
      const value = Number(cents);
      return Number.isFinite(value) && value > 0 ? (value / 100).toFixed(2) : '';
    }

    formatMoney(cents, currency = this.currency) {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || this.currency,
        minimumFractionDigits: 2,
      }).format((Number(cents) || 0) / 100);
    }

    formatDate(value) {
      if (!value) return 'Not set';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      }).format(date);
    }

    formatDateTime(value) {
      if (!value) return 'Not set';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }).format(date);
    }

    getSafeDropboxUrl(file) {
      const rawUrl = (file?.dropbox_url || '').trim();
      if (!rawUrl) return '';

      try {
        const url = new URL(rawUrl);
        const hostname = url.hostname.toLowerCase();
        if (url.protocol !== 'https:') return '';
        if (hostname !== 'dropbox.com' && !hostname.endsWith('.dropbox.com')) return '';
        return url.href;
      } catch (_unusedError) {
        return '';
      }
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    escapeAttribute(value) {
      return this.escapeHtml(value);
    }
  }

  customElements.define('admin-barter-proposals', AdminBarterProposals);
}
